import { Link } from "react-router-dom";
import { ArrowRightIcon, CircleIcon } from "lucide-react";

import { LogoIcon } from "@/components/LogoIcon";
import { Heading } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { asPlatforms } from "../../../shared/platforms";
import type { Campaign } from "../../../shared/types";
import { opportunityPriceLabel, opportunityRecencyLabel, pitchCountLabel } from "../list";

type OpportunityCardProps = {
    campaign: Campaign;
    companySector?: string;
};

/** One index campaign. The whole card goes to campaign detail — Pitch is a label, not a nested link. */
export function OpportunityCard({ campaign, companySector }: OpportunityCardProps) {
    const platforms = asPlatforms(campaign.campaignPlatform);
    const price = opportunityPriceLabel(campaign);
    const recency = opportunityRecencyLabel(campaign.pitchingOpenChangedAt);
    const isOpen = campaign.pitchingOpen === true;

    return (
        <Link
            to={`/campaign/${campaign.id}`}
            className="block rounded-xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
            <Card size="sm" className="gap-0 hover:bg-muted/50">
                <CardContent className="space-y-2">
                    {/* Brand and recency share the top line. */}
                    <div className="flex items-start justify-between gap-4">
                        <span className="text-base/6 uppercase text-muted-foreground">
                            {campaign.booker?.company}
                        </span>
                        {recency ? <span className="shrink-0 text-xs/4">{recency}</span> : null}
                    </div>
                    <Heading type="h4" tag="h2" className="font-bold text-lg/7">
                        {campaign.campaignName}
                    </Heading>
                    {/* Sector badge, closed mark, and platform marks sit on one line. */}
                    <div className="flex flex-wrap items-center gap-2.5">
                        {!isOpen ? (
                            <Badge variant="destructive">
                                <CircleIcon className="fill-current" />
                                Closed
                            </Badge>
                        ) : null}
                        {companySector ? <Badge variant="purple">{companySector}</Badge> : null}
                        {platforms.map((platform) => (
                            <LogoIcon key={platform} brand={platform} className="size-5" title={platform} />
                        ))}
                    </div>
                    <p className="text-base/6 text-muted-foreground">{price}</p>
                    <Separator />
                    {/* Pitch count and the Pitch label share the footer line. */}
                    <div className="flex items-center justify-between">
                        <span className="text-base/none">{pitchCountLabel(campaign.pitchCount)}</span>
                        {isOpen ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                                Pitch
                                <ArrowRightIcon className="size-3" aria-hidden />
                            </span>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

/** Brand, name, badge, price, and footer rhythm for an opportunity-card wait. */
export function OpportunityCardSkeleton() {
    return (
        <Card size="sm" className="gap-0">
            <CardContent className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-7 w-48" />
                <div className="flex flex-wrap items-center gap-2.5">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="size-5" />
                    <Skeleton className="size-5" />
                </div>
                <Skeleton className="h-6 w-36" />
                <Separator />
                <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-12" />
                </div>
            </CardContent>
        </Card>
    );
}

/** Same card grid for Active Opportunities and Recently closed. */
export function OpportunityCardGrid({
    campaigns,
    sectorByBookerUid,
    isLoading,
}: {
    campaigns: Campaign[];
    sectorByBookerUid: Map<string, string>;
    isLoading?: boolean;
}) {
    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {isLoading
                ? Array.from({ length: 6 }, (_, index) => <OpportunityCardSkeleton key={index} />)
                : campaigns.map((campaign) => (
                    <OpportunityCard
                        key={campaign.id}
                        campaign={campaign}
                        companySector={
                            campaign.booker?.uid
                                ? sectorByBookerUid.get(campaign.booker.uid)
                                : undefined
                        }
                    />
                ))}
        </div>
    );
}
